import MuxPlayer from '@mux/mux-player-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { localize } from '../../i18n/script';
import { courseApi, type PlaybackVideo } from '../../services/courseApi';
import { getLocalizedTitle } from '../../utils/activityTransform';

// On-platform watch page for an entitled recorded course. Only purchasers can
// load playback tokens (the backend enforces the entitlement); screen sharing
// is fine, account switching is not (tokens are per-user, short-lived).
export default function CourseWatch() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, getToken } = useAuth();
  const { i18n } = useTranslation();

  const [title, setTitle] = useState('');
  const [videos, setVideos] = useState<PlaybackVideo[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notOwned, setNotOwned] = useState(false);

  const envKey = import.meta.env.VITE_MUX_DATA_ENV_KEY as string | undefined;

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate(`/login?redirect=/courses/${slug}/watch`);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const token = await getToken();
        if (!token) {
          navigate(`/login?redirect=/courses/${slug}/watch`);
          return;
        }
        // Resolve playback by slug via the entitled endpoint: works even if the
        // course was delisted, as long as the user owns it (permanent access).
        const {
          titleEN,
          titleCN,
          videos: playback,
        } = await courseApi.getPlaybackBySlug(slug as string, token);
        if (cancelled) return;
        setTitle(getLocalizedTitle({ titleEN, titleCN }, i18n.language));
        setVideos(playback);
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load the course';
        // The backend returns 403 with an access message when not entitled.
        if (/access/i.test(message)) setNotOwned(true);
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, isAuthenticated, authLoading, getToken, navigate, i18n.language]);

  if (authLoading || loading) {
    return <div className="mx-auto max-w-4xl px-4 py-16 text-center text-gray-500">Loading...</div>;
  }

  if (notOwned) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="mb-3 text-2xl font-semibold text-gray-900">You do not own this course</h1>
        <p className="mb-6 text-gray-600">
          Purchase the course to watch it anytime, with permanent access.
        </p>
        <Link
          to={`/courses/${slug}`}
          className="inline-block rounded-lg bg-orange-500 px-6 py-3 font-medium text-white hover:bg-orange-600"
        >
          View course
        </Link>
      </div>
    );
  }

  if (error) {
    return <div className="mx-auto max-w-2xl px-4 py-16 text-center text-red-600">{error}</div>;
  }

  if (videos.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center text-gray-600">
        This course has no videos available to watch yet.
      </div>
    );
  }

  const active = videos[activeIndex];

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <h1 className="mb-4 text-2xl font-semibold text-gray-900">{title}</h1>

      <div className="overflow-hidden rounded-xl bg-black">
        <MuxPlayer
          key={active.videoId}
          playbackId={active.playbackId}
          tokens={{ playback: active.token }}
          streamType="on-demand"
          accentColor="#f97316"
          metadata={{
            video_id: active.videoId,
            video_title: localize(active.titleCN, active.titleEN, i18n.language),
          }}
          {...(envKey ? { envKey } : {})}
          style={{ width: '100%', aspectRatio: '16 / 9' }}
        />
      </div>

      {videos.length > 1 && (
        <div className="mt-6">
          <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-gray-500">
            Segments
          </h2>
          <ol className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200">
            {videos.map((v, i) => (
              <li key={v.videoId}>
                <button
                  type="button"
                  onClick={() => setActiveIndex(i)}
                  className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm ${
                    i === activeIndex ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <span className="text-gray-400">{i + 1}</span>
                  <span className="flex-1">{localize(v.titleCN, v.titleEN, i18n.language)}</span>
                  {i === activeIndex && <span className="text-xs">Now playing</span>}
                </button>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
